import { useRouter } from "next/router";

import { RhCard, RhIcon, RhToast } from "@rhythm-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useContext, useMemo } from "react";

import { AuthContext } from "@/context/AuthContext";

import { JobType } from "@/types/job";

import api from "@/services/api";

import { HOST_URL } from "@/constants";

import formatExperienceRange from "@/helpers/formatExperienceRange";

import Button from "@/components/utils/Button";

dayjs.extend(relativeTime);

const JobOverview = ({
  jobDetail,
  className,
  setIsRevokeApplicationForJobIdDialogOpen,
  isPagePublic,
}: {
  jobDetail: JobType | null;
  className: string;
  setIsRevokeApplicationForJobIdDialogOpen: React.Dispatch<
    React.SetStateAction<null | number>
  >;
  isPagePublic: boolean | undefined;
}) => {
  return (
    <div className={className}>
      <JobOverviewDisplay
        setIsRevokeApplicationForJobIdDialogOpen={
          setIsRevokeApplicationForJobIdDialogOpen
        }
        jobDetail={jobDetail}
        isPagePublic={isPagePublic}
      />
    </div>
  );
};

export default JobOverview;

const JobOverviewDisplay = ({
  jobDetail,
  setIsRevokeApplicationForJobIdDialogOpen,
  isPagePublic,
}: {
  jobDetail: JobType | null;
  setIsRevokeApplicationForJobIdDialogOpen: React.Dispatch<
    React.SetStateAction<null | number>
  >;
  isPagePublic: boolean | undefined;
}) => {
  const { auth } = useContext(AuthContext);

  const queryClient = useQueryClient();

  const postCandidateJobApplication = useMutation({
    mutationFn: (data: {
      job_requirement: number | undefined;
      // accepted_by: number;
    }) => api.company.postCandidateJobApplication(data),
    onSuccess: (_, param) => {
      queryClient.invalidateQueries({
        queryKey: ["job", { id: param.job_requirement }],
      });
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });
      RhToast.success("Applied to job", { position: "bottom-right" });
    },
  });

  const router = useRouter();
  const handleCreateApplication = async () => {
    if (isPagePublic && jobDetail) {
      router.push({
        pathname: "/login",
        query: { redirect: `${HOST_URL}/jobs?id=${jobDetail.id}` },
      });
    } else {
      if (!auth.candidateId) return;

      postCandidateJobApplication.mutate({
        job_requirement: jobDetail?.id,
        // accepted_by: auth.candidateId,
      });
    }
  };

  if (jobDetail === null)
    return (
      <div className="flex items-center justify-center">job not found</div>
    );

  const candidateWorkTypePreference = useMemo(() => {
    return (
      <>
        {jobDetail.work_preference_new?.map((item, idx, arr) => (
          <React.Fragment key={idx}>
            <p>{item}</p>
            {arr.length - 1 !== idx && <p>{"•"}</p>}
          </React.Fragment>
        )) ?? "-"}
      </>
    );
  }, [jobDetail.work_preference_new]);

  const candidateLocationPreference = useMemo(
    () =>
      "(" + jobDetail.location_new.map((city) => city.name).join(", ") + ")",

    [jobDetail.location_new]
  );

  return (
    <div className="p-tm2">
      <RhCard className="p-tm2">
        <div className="flex flex-col gap-tm2">
          <div className="h-[152px] w-[152px] flex items-center justify-between rounded-md border mr-tm2 px-tnano">
            {jobDetail.company.logo && (
              <img src={jobDetail.company.logo} className="w-full" />
            )}
          </div>
          {/* {JSON.stringify(jobDetail)} */}
          <div>
            <h2>{jobDetail?.title}</h2>
            <p className="text-light">{jobDetail.company?.name}</p>
          </div>

          <div className="flex items-center gap-tnano">
            <RhIcon icon={"bx:briefcase-alt"} className="text-xl"></RhIcon>
            <p>
              {formatExperienceRange.number({
                min: jobDetail.min_exp,
                max: jobDetail.max_exp,
              })}
            </p>
          </div>

          {/* <div className="flex items-center gap-tnano">
            <RhIcon icon={"tabler:cash-banknote"} className="text-xl"></RhIcon>
            <div className="flex items-center gap-tsm">
              <p className="flex flex-wrap">{ctcRange}</p>
            </div>
          </div> */}

          <div className="flex flex-wrap items-center gap-tnano">
            <RhIcon icon={"ph:map-pin"} className="text-xl"></RhIcon>
            {candidateWorkTypePreference}

            <p className="text-light">
              {jobDetail.work_preference_new &&
                jobDetail.location_new?.length > 0 && (
                  <>{candidateLocationPreference}</>
                )}
            </p>
          </div>
        </div>
      </RhCard>
      {jobDetail.is_applied ? (
        <Button
          className="mt-tnano"
          layout="outline"
          block
          onClick={() => setIsRevokeApplicationForJobIdDialogOpen(jobDetail.id)}
        >
          Revoke Application
        </Button>
      ) : (
        <Button
          loading={postCandidateJobApplication.isLoading}
          className="mt-tnano"
          block
          onClick={handleCreateApplication}
        >
          Apply
        </Button>
      )}
    </div>
  );
};
